import { RealtimeService } from './../../../services/realtime.service';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import {
	trigger, transition, style, animate, AnimationBuilder,
	AnimationPlayer, AnimationFactory
} from '@angular/animations';
import { ViewSessionEvent } from './models/view-session-event.model';

@Component({
	selector: 'realtime-audience-ticker',
	templateUrl: './realtime-audience-ticker.component.html',
	styleUrls: ['./realtime-audience-ticker.component.scss'],
	animations: [
		trigger('moveIn', [
			transition(':enter', [
				style({ transform: 'translateX(1000px)' }),
				animate(1000, style({ transform: 'translateX(0px)' }))
			])
		])
	]
})

export class RealtimeAudienceTickerComponent implements OnInit, OnDestroy {
	@ViewChild('mainContainer') mainContainer: ElementRef;
	@ViewChild('scrollableContainer') scrollableContainer: ElementRef;

	viewEvents: ViewSessionEvent[];

	private viewEventsToAdd: ViewSessionEvent[];
	private cnt = 0;
	private prevEvent: ViewSessionEvent;
	private player: AnimationPlayer;
	private playerDone = true;
	private subscription_EventAdded: Subscription;
	private initialCallFinished: Subscription;
	private lostFocus: Subscription;

	// events that will be shown -> add in template if you add an event here:
	private eventsToShow: string[] = ['rs', 'st', 'pa', 'xst', 'rp', 'se', 'fn', 'add', 'xcl', 'spacer'];
	private iconWidth = 54;													// hardcoded width for now
	private iconAmountToShow = 18;											// will be changed if container width changes
	private animationTime = 2000;											// time per animation
	private optimizeConsecutiveDoubleEvents = false;						// can be true to get optimization
	private insertTimeTimer: any;

	constructor(protected realtimeService: RealtimeService, protected animBuilder: AnimationBuilder) {
		this.viewEvents = [];
		this.viewEventsToAdd = [];
	}

	ngOnInit() {
		this.initialCallFinished = this.realtimeService.initialCallFinished.subscribe(() => {
			if (!this.subscription_EventAdded) {
				this.viewEvents = [];
				this.viewEventsToAdd = [];

				this.insertTimeTimer = setInterval(() => {
					this.insertTime();
				}, 1000 * 60);
				for (let i = 0; i < 50 || i === this.iconAmountToShow; i++) {
					const fakeEvent: ViewSessionEvent = { time: new Date(), to: 0 };
					fakeEvent.ev = 'spacer';
					this.showEvent(fakeEvent);
				}

				this.subscription_EventAdded = this.realtimeService.eventAdded.subscribe((event: ViewSessionEvent) => {
					if (event && this.optimized(event)) {
						this.showEvent(event);
						this.prevEvent = event;
					}
				});
			}
		});

		this.lostFocus = this.realtimeService.lostFocus.subscribe(() => {
			if (this.subscription_EventAdded) {
				this.subscription_EventAdded.unsubscribe();
				this.subscription_EventAdded = null;
			}
			if (this.insertTimeTimer) {
				clearInterval(this.insertTimeTimer);
			}
		});
	}

	ngOnDestroy() {
		this.subscription_EventAdded.unsubscribe();
		if (this.player) {
			this.player.finish();
			this.player.destroy();
		}
		if (this.insertTimeTimer) {
			clearInterval(this.insertTimeTimer);
		}
		if (this.initialCallFinished) {
			this.initialCallFinished.unsubscribe();
		}
		if (this.lostFocus) {
			this.lostFocus.unsubscribe();
		}
	}

	logEvent(event: ViewSessionEvent): void {
		/* eslint-disable-next-line no-console */
		console.log('Ticker event: ', event);
	}

	private optimized(event: ViewSessionEvent): boolean {
		let ret = true;
		if (this.optimizeConsecutiveDoubleEvents && this.prevEvent &&
			this.prevEvent.ev === event.ev && this.prevEvent.et === event.et && event.ev !== 'spacer') {
			ret = false;
		}
		if (this.eventsToShow.indexOf(event.ev) === -1) {
			ret = false;
		}
		return ret;
	}

	private showEvent(event: ViewSessionEvent) {
		if (!this.scrollableContainer) {
			return;
		}

		// check mainContainer size
		this.iconAmountToShow = Math.ceil(this.mainContainer.nativeElement.getBoundingClientRect().width / this.iconWidth);

		if (this.cnt >= this.iconAmountToShow) {
			if (this.playerDone) {
				this.animateNewEvents();
			} else {
				this.viewEventsToAdd.push(event);
				if (this.viewEventsToAdd.length > this.iconAmountToShow) {
					this.viewEventsToAdd.shift();
				}
			}
		} else {
			this.viewEvents.push(event);
			this.cnt++;
		}
	}

	private animateNewEvents(): void {
		const nrOfEvents = this.viewEventsToAdd.length;
		for (let i = 0; i < nrOfEvents; i++) {
			this.viewEvents.push(this.viewEventsToAdd.shift());
		}
		this.createAnimationPlayer(this.iconWidth * nrOfEvents, this.animationTime);
	}

	private createAnimationPlayer(pos: number, time: number): void {
		const definition: AnimationFactory = this.animBuilder.build([
			style({ transform: 'translateX(0)' }),
			animate(time + 'ms cubic-bezier(0.215, 0.61, 0.355, 1)', style({
				transform: 'translateX(-' + Math.abs(pos) + 'px)'
			}))
		]);

		this.playerDone = false;
		if (this.player) {
			this.player.finish();
			this.player.destroy();
		}
		this.player = definition.create(this.scrollableContainer.nativeElement);
		this.player.play();

		this.player.onDone(() => {

			while (this.viewEvents.length > this.iconAmountToShow) {
				this.viewEvents.shift();
			}
			this.resetAnimation();
			this.playerDone = true;
		});
	}

	private resetAnimation(): void {
		const definition: AnimationFactory = this.animBuilder.build([
			style({ transform: 'translateX(0)' })
		]);

		this.player = definition.create(this.scrollableContainer.nativeElement);
		this.player.play();
	}

	private insertTime(): void {
		const d: Date = new Date();

		const fakeEvent: ViewSessionEvent = { time: new Date(), to: 0 };
		fakeEvent.date = d;
		fakeEvent.ev = 'time';

		this.showEvent(fakeEvent);
	}
}
